import { ReactNode, useEffect, useRef, useState } from 'react'
import { Button, Stack } from '@mui/material'
import { ExpandLess, ExpandMore } from '@workwave-tidal/core/icons'
import { NullValue } from '../NullValue'
import { TagValue } from '../TagValue'
import { useTexts } from './useTexts'

interface Props {
  tags: string[]
  maxHeightPx: number
  extraTagContent?: ReactNode
}

export function TagsList(props: Props) {
  const { tags, maxHeightPx, extraTagContent = null } = props
  const containerRef = useRef<HTMLDivElement | null>(null)
  const texts = useTexts()

  const [isOverflowing, setIsOverflowing] = useState(false)
  const [collapsed, setCollapsed] = useState(true)

  const toggleCollapse = () => {
    setCollapsed(p => !p)
  }

  const showShowMoreButton = isOverflowing && collapsed
  const showShowLessButton = !collapsed

  useEffect(() => {
    const element = containerRef.current
    if (!element) return

    const checkOverflow = () => {
      const overflow = element.scrollHeight - element.clientHeight > 1
      setIsOverflowing(overflow)
    }

    // Check initially
    checkOverflow()

    // Set up ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow()
    })

    resizeObserver.observe(element)

    // Cleanup
    return () => {
      resizeObserver.disconnect()
    }
  }, [tags, maxHeightPx])

  if (tags.length === 0) return <NullValue />

  const maxHeight = !collapsed ? 'none' : maxHeightPx

  return (
    <Stack sx={{ gap: 1, alignItems: 'flex-start' }}>
      <Stack
        ref={containerRef}
        sx={{ flexDirection: 'row', flexWrap: 'wrap', overflow: 'hidden', gap: 1, maxHeight }}
      >
        {tags.map(t => (
          <TagValue key={t}>{t}</TagValue>
        ))}
      </Stack>
      {extraTagContent}
      {showShowMoreButton && (
        <Button onClick={toggleCollapse} endIcon={<ExpandMore />}>
          {texts.showMore}
        </Button>
      )}
      {showShowLessButton && (
        <Button onClick={toggleCollapse} endIcon={<ExpandLess />}>
          {texts.showLess}
        </Button>
      )}
    </Stack>
  )
}
